/**
 * uiStore — Plan E E.5 (viewMode) + 1.88.0 (interfaceFontSize) + 1.91.0 (editorFontSize)
 *
 * SPA-wide UI state. Persists across page loads via Zustand's `persist`
 * middleware so reopening the tab restores the last view + font sizes.
 *
 * Fields (all per-browser, NOT per-tenant — distinct from chatPrefsStore
 * which is per-tenant):
 *   - viewMode: 'designer' | 'terminals' (Plan E E.5)
 *   - interfaceFontSize: integer px in [12, 22], default 16 (1.88.0;
 *     applied to document.documentElement.style.fontSize so Tailwind's
 *     rem-based text-* classes scale across the whole SPA chrome).
 *   - editorFontSize: integer px in [10, 22], default 14 (1.91.0;
 *     passed to Monaco's `options.fontSize`. Monaco does NOT inherit
 *     html font-size — it has its own font-size prop, so this knob is
 *     genuinely independent from interfaceFontSize per user request).
 *
 * The Pi Chat panel has its OWN per-tenant fontSize in chatPrefsStore that
 * acts as an override INSIDE the Pi chat surface (via CSS variable
 * --vibe-pi-chat-font-size). The three co-exist:
 *   - interfaceFontSize scales SPA chrome (top bar, sidebar, panels)
 *   - editorFontSize controls Monaco's content area
 *   - Pi Chat font size fine-tunes the chat panel relative to global
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// 1.168.0 Phase 1 — 3rd top-tab "Knowledge" added between Designer and
// Terminals. See docs/20260520/20260520_180000_DESIGN_KNOWLEDGE_TAB_TENANT_CORPUS_DASHBOARD.md
// (the Knowledge shell is a tenant's read-only view of their corpus across
// MIND/CORTEX/BRAIN/SIGHT/COGNITION/Obsidian; Phase 1 is a placeholder shell only).
//
// Briefing Room M1 (2026-05-31) — 4th top-tab "Modules" added at the end.
// Lazy-mounts loadable vibe-designer modules (first: The Briefing Room).
// See apps/briefing-room-module/schema/host_probe.md for the host-detection
// contract module SPAs use at boot.
//
// Dashboard tab P1 (§11 LOCKED 2026-07-28) — 6th top-tab 'dashboard' added as
// the LEFTMOST primary tab and the first-run default viewMode: the user-facing
// onboarding "Business OS" home. The rest of the tabs are agent-oriented / dense
// by design; Dashboard is the human-first front door. Design doc:
// docs/20260728/dashboard_onboarding_design/20260728_DESIGN_DASHBOARD_TAB_ONBOARDING_BUSINESS_OS.md
export type ViewMode = 'dashboard' | 'designer' | 'terminals' | 'knowledge' | 'modules' | 'deploys';

// 1.127.0 Phase 2d: MobileStackedShell active-pane discriminator. Drives
// the display:contents/none flip for 4 panes (5 in 1.127.0; 1.128.0 Phase
// 2e moved 'files' to a dedicated FilesDrawer). Default 'chat' per
// worldview §3.1 (Pi chat is the most useful surface on phone).
export type MobilePanel = 'chat' | 'editor' | 'preview' | 'info';

// 1.422.0 — Modules-tab concurrent tiling. A tile may be docked to one edge
// (half-screen pane; the rest re-flow into the other half).
export type ModuleDockEdge = 'left' | 'right' | 'top' | 'bottom';

export interface DockedModule {
  id: string;
  edge: ModuleDockEdge;
}

const MODULE_DOCK_EDGES: readonly ModuleDockEdge[] = ['left', 'right', 'top', 'bottom'];

// 1.552.0 — ranges widened (were 12-22). Operators asked for the full spread
// of usable sizes; 11-28 covers dense laptops through large / low-vision chrome.
export const INTERFACE_FONT_SIZE_RANGE = {
  min: 11,
  max: 28,
  default: 16,
} as const;

// 1.91.0: Monaco editor font size range. Lower minimum than interface because
// the editor benefits from tighter density on laptops.
// 1.552.0 — widened to 9-32 (was 10-22); Monaco reads it directly, so a large
// upper bound is genuinely useful for presenting / pairing. Default 14 matches
// Monaco's historical default + the previous hardcoded value in EditorPanel.tsx:322.
export const EDITOR_FONT_SIZE_RANGE = {
  min: 9,
  max: 32,
  default: 14,
} as const;

function clampFontSize(n: number): number {
  return Math.max(
    INTERFACE_FONT_SIZE_RANGE.min,
    Math.min(INTERFACE_FONT_SIZE_RANGE.max, Math.round(n)),
  );
}

function clampEditorFontSize(n: number): number {
  return Math.max(
    EDITOR_FONT_SIZE_RANGE.min,
    Math.min(EDITOR_FONT_SIZE_RANGE.max, Math.round(n)),
  );
}

interface UIState {
  viewMode: ViewMode;
  interfaceFontSize: number;
  editorFontSize: number;
  // 2026-07-08: Designer editor pane collapsed (hidden → the preview fills the
  // editor/preview split). PERSISTED. User-toggled via the editor header's Hide
  // control; auto-expands when a file is opened. Default false (editor visible).
  editorCollapsed: boolean;
  // 2026-07-11: Designer bottom Info panel (Problems/Output/Terminal/Git)
  // closed. Its X (and collapse chevron) close it; the preview toolbar's "Show
  // panel" reopens it. PERSISTED. Default false (panel visible).
  infoCollapsed: boolean;
  // 1.126.0 Phase 2c: mobile drawer state. Transient — NOT persisted via
  // partialize. Drawer is touch-only; user starts each session with it
  // closed (no need to survive reload).
  mobileMenuOpen: boolean;
  // 1.127.0 Phase 2d: MobileStackedShell active pane (Pi Chat, Editor,
  // Preview, Files, Info). Default 'chat'. Transient (partialize excludes).
  activeMobilePanel: MobilePanel;
  // 1.127.0 Phase 2d: touch detection cached at mount by useIsTouch hook.
  // Reads window.matchMedia('(pointer: coarse)').matches; subscribes to
  // change events so DevTools "Toggle device toolbar" works. Transient.
  isTouch: boolean;
  // 1.128.0 Phase 2e: FilesDrawer visibility. Files moved from
  // MobileStackedShell tab bar to a dedicated left-edge slide-in drawer
  // per master plan §3.1. Transient. NO toggle setter (no caller).
  filesDrawerOpen: boolean;
  // 1.166.0: voice settings modal open state, lifted out of VoicePanel's
  // local useState so VoiceAgentQuickSwitcher's per-row settings icon can
  // open it (after first switching to the row's tab). Transient.
  voiceSettingsOpen: boolean;
  // Modules tab — FOCUSED tile id (1.422.0: was "the module currently
  // shown" in the single-module host; retained as the focus mirror for
  // back-compat). null = nothing focused. PERSISTED.
  activeModuleId: string | null;
  // 1.422.0 — Modules-tab concurrent tiling. Ordered OPEN tiles (max one
  // instance per module — structural: openModule on an open id only
  // focuses). All three PERSISTED via uiStorePartialize.
  openModuleIds: string[];
  dockedModule: DockedModule | null;
  maximizedModuleId: string | null;
  setViewMode: (mode: ViewMode) => void;
  toggleViewMode: () => void;
  setInterfaceFontSize: (size: number) => void;
  setEditorFontSize: (size: number) => void;
  setEditorCollapsed: (collapsed: boolean) => void;
  setInfoCollapsed: (collapsed: boolean) => void;
  setMobileMenuOpen: (open: boolean) => void;
  toggleMobileMenuOpen: () => void;
  setActiveMobilePanel: (panel: MobilePanel) => void;
  setIsTouch: (isTouch: boolean) => void;
  setFilesDrawerOpen: (open: boolean) => void;
  setVoiceSettingsOpen: (open: boolean) => void;
  setActiveModule: (moduleId: string | null) => void;
  /** 1.422.0 — open (append + focus) a module tile; idempotent on open ids. */
  openModule: (moduleId: string) => void;
  /** 1.422.0 — close a tile; clears dock/maximize if it held them and
   * refocuses the last remaining tile (null when none). */
  closeModule: (moduleId: string) => void;
  /** 1.422.0 — focus an already-open tile (no-op when not open). */
  focusModule: (moduleId: string) => void;
  /** 1.422.0 — dock a tile to an edge, or clear its dock with null. */
  dockModule: (moduleId: string, edge: ModuleDockEdge | null) => void;
  /** 1.422.0 — solo/restore a tile (others stay mounted, hidden). */
  toggleMaximizeModule: (moduleId: string) => void;
  reset: () => void;
}

/**
 * 1.422.0 — coerce a persisted module-layout payload: migrates pre-tiling
 * payloads (activeModuleId only → seed openModuleIds) and drops corrupt
 * shapes (non-array openModuleIds, invalid dock edge, non-string ids).
 * Exported for unit tests; called from onRehydrateStorage.
 */
export function coerceModuleLayout(state: {
  activeModuleId?: unknown;
  openModuleIds?: unknown;
  dockedModule?: unknown;
  maximizedModuleId?: unknown;
}): {
  activeModuleId: string | null;
  openModuleIds: string[];
  dockedModule: DockedModule | null;
  maximizedModuleId: string | null;
} {
  const activeModuleId =
    typeof state.activeModuleId === 'string' && state.activeModuleId.length > 0
      ? state.activeModuleId
      : null;
  let openModuleIds = Array.isArray(state.openModuleIds)
    ? (state.openModuleIds as unknown[]).filter(
        (x): x is string => typeof x === 'string' && x.length > 0,
      )
    : [];
  // Migration: pre-tiling payload carried only activeModuleId.
  if (openModuleIds.length === 0 && activeModuleId) {
    openModuleIds = [activeModuleId];
  }
  const rawDock = state.dockedModule as { id?: unknown; edge?: unknown } | null | undefined;
  const dockedModule: DockedModule | null =
    rawDock &&
    typeof rawDock.id === 'string' &&
    openModuleIds.includes(rawDock.id) &&
    (MODULE_DOCK_EDGES as readonly string[]).includes(rawDock.edge as string)
      ? { id: rawDock.id, edge: rawDock.edge as ModuleDockEdge }
      : null;
  const maximizedModuleId =
    typeof state.maximizedModuleId === 'string' && openModuleIds.includes(state.maximizedModuleId)
      ? state.maximizedModuleId
      : null;
  return { activeModuleId, openModuleIds, dockedModule, maximizedModuleId };
}

/**
 * partialize — exported so the persist contract is unit-testable. Adding new
 * fields to `UIState` MUST NOT auto-persist them; only `viewMode`,
 * `interfaceFontSize`, and `editorFontSize` are durable as of 1.91.0.
 */
export const uiStorePartialize = (
  state: UIState,
): {
  viewMode: ViewMode;
  activeModuleId: string | null;
  openModuleIds: string[];
  dockedModule: DockedModule | null;
  maximizedModuleId: string | null;
  interfaceFontSize: number;
  editorFontSize: number;
  editorCollapsed: boolean;
  infoCollapsed: boolean;
} => ({
  viewMode: state.viewMode,
  activeModuleId: state.activeModuleId,
  // 1.422.0 — module tiling layout survives reload.
  openModuleIds: state.openModuleIds,
  dockedModule: state.dockedModule,
  maximizedModuleId: state.maximizedModuleId,
  interfaceFontSize: state.interfaceFontSize,
  editorFontSize: state.editorFontSize,
  editorCollapsed: state.editorCollapsed,
  infoCollapsed: state.infoCollapsed,
});

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Dashboard tab P1 (§11.1 LOCKED 2026-07-28): first-run default is the
      // Dashboard onboarding home. Returning users keep their persisted
      // viewMode (persist middleware rehydrates it), so this default only
      // applies to a brand-new browser with no stored UI state.
      viewMode: 'dashboard',
      interfaceFontSize: INTERFACE_FONT_SIZE_RANGE.default,
      editorFontSize: EDITOR_FONT_SIZE_RANGE.default,
      editorCollapsed: false,
      infoCollapsed: false,
      mobileMenuOpen: false,
      activeMobilePanel: 'chat',
      isTouch: false,
      filesDrawerOpen: false,
      voiceSettingsOpen: false,
      activeModuleId: null,
      openModuleIds: [],
      dockedModule: null,
      maximizedModuleId: null,

      setViewMode: (mode) => set({ viewMode: mode }),

      // 1.168.0 Phase 1: toggleViewMode cycles through all modes.
      // Briefing Room M1 (2026-05-31): extended to 4-way cycle including
      // 'modules' (designer → terminals → knowledge → modules → designer).
      // R1 reviewer concern #1.
      // Dashboard tab P1 (2026-07-28): 'dashboard' prepended as the leftmost
      // tab, so the cycle starts at dashboard and wraps deploys → dashboard.
      toggleViewMode: () =>
        set({
          viewMode: (() => {
            switch (get().viewMode) {
              case 'dashboard':
                return 'designer';
              case 'designer':
                return 'terminals';
              case 'terminals':
                return 'knowledge';
              case 'knowledge':
                return 'modules';
              case 'modules':
                return 'deploys';
              case 'deploys':
                return 'dashboard';
            }
          })(),
        }),

      setInterfaceFontSize: (size) => set({ interfaceFontSize: clampFontSize(size) }),

      setEditorFontSize: (size) => set({ editorFontSize: clampEditorFontSize(size) }),

      setEditorCollapsed: (collapsed) => set({ editorCollapsed: collapsed }),
      setInfoCollapsed: (collapsed) => set({ infoCollapsed: collapsed }),

      setMobileMenuOpen: (open) => set({ mobileMenuOpen: open }),

      toggleMobileMenuOpen: () => set({ mobileMenuOpen: !get().mobileMenuOpen }),

      setActiveMobilePanel: (panel) => set({ activeMobilePanel: panel }),

      setIsTouch: (isTouch) => set({ isTouch }),

      setFilesDrawerOpen: (open) => set({ filesDrawerOpen: open }),

      setVoiceSettingsOpen: (open) => set({ voiceSettingsOpen: open }),

      // 1.422.0 back-compat: selecting a module OPENS it (append + focus).
      // null clears focus only (open tiles are managed via closeModule).
      setActiveModule: (moduleId) => {
        if (moduleId === null) {
          set({ activeModuleId: null });
          return;
        }
        get().openModule(moduleId);
      },

      openModule: (moduleId) => {
        const { openModuleIds } = get();
        if (openModuleIds.includes(moduleId)) {
          set({ activeModuleId: moduleId });
          return;
        }
        set({
          openModuleIds: [...openModuleIds, moduleId],
          activeModuleId: moduleId,
        });
      },

      closeModule: (moduleId) => {
        const { openModuleIds, dockedModule, maximizedModuleId, activeModuleId } = get();
        const next = openModuleIds.filter((id) => id !== moduleId);
        set({
          openModuleIds: next,
          dockedModule: dockedModule?.id === moduleId ? null : dockedModule,
          maximizedModuleId: maximizedModuleId === moduleId ? null : maximizedModuleId,
          activeModuleId:
            activeModuleId === moduleId ? (next[next.length - 1] ?? null) : activeModuleId,
        });
      },

      focusModule: (moduleId) => {
        if (get().openModuleIds.includes(moduleId)) {
          set({ activeModuleId: moduleId });
        }
      },

      dockModule: (moduleId, edge) => {
        if (edge === null) {
          if (get().dockedModule?.id === moduleId) set({ dockedModule: null });
          return;
        }
        if (!get().openModuleIds.includes(moduleId)) return;
        set({ dockedModule: { id: moduleId, edge } });
      },

      toggleMaximizeModule: (moduleId) => {
        if (!get().openModuleIds.includes(moduleId)) return;
        set({
          maximizedModuleId: get().maximizedModuleId === moduleId ? null : moduleId,
        });
      },

      reset: () =>
        set({
          // Dashboard tab P1 (2026-07-28): reset returns to the first-run default.
          viewMode: 'dashboard',
          interfaceFontSize: INTERFACE_FONT_SIZE_RANGE.default,
          editorFontSize: EDITOR_FONT_SIZE_RANGE.default,
          editorCollapsed: false,
          infoCollapsed: false,
          mobileMenuOpen: false,
          activeMobilePanel: 'chat',
          isTouch: false,
          filesDrawerOpen: false,
          voiceSettingsOpen: false,
          activeModuleId: null,
          openModuleIds: [],
          dockedModule: null,
          maximizedModuleId: null,
        }),
    }),
    {
      name: 'vibe-designer-ui',
      partialize: uiStorePartialize,
      // 1.88.0: defensive coercion of a corrupt persisted payload (older
      // schema, manual localStorage edit, out-of-range number). Runs once
      // after persist reads from storage so first paint never gets a bogus
      // inline-style value on the document element.
      onRehydrateStorage: () => (state) => {
        if (state) {
          const s = state as UIState;
          const rawIfc = s.interfaceFontSize;
          s.interfaceFontSize =
            typeof rawIfc === 'number' && Number.isFinite(rawIfc)
              ? clampFontSize(rawIfc)
              : INTERFACE_FONT_SIZE_RANGE.default;
          const rawEd = s.editorFontSize;
          s.editorFontSize =
            typeof rawEd === 'number' && Number.isFinite(rawEd)
              ? clampEditorFontSize(rawEd)
              : EDITOR_FONT_SIZE_RANGE.default;
          // 2026-07-08 — coerce editorCollapsed to a strict boolean (older
          // payloads lack it → undefined → editor visible).
          s.editorCollapsed = s.editorCollapsed === true;
          s.infoCollapsed = s.infoCollapsed === true;
          // 1.422.0 — migrate pre-tiling payloads + drop corrupt layouts.
          Object.assign(s, coerceModuleLayout(s));
        }
      },
    },
  ),
);
